import React, { forwardRef, useEffect, useRef, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactQuill from 'react-quill-new';
import DatePicker from 'react-datepicker';
import 'react-quill-new/dist/quill.snow.css';
import 'react-datepicker/dist/react-datepicker.css';
import Upload from '../../../assets/icons/message-manager/upload.svg';
import RadioCheck from '../../../components/MessageManager/RadioCheck';
import NumberInput from '../../../components/MessageManager/NumberInput';
import dateicon from '../../../assets/icons/message-manager/date.svg';
import timeicon from '../../../assets/icons/message-manager/time.svg';
import commonFunctions from '../../../utils/commonFunctions';

const messageStyleOptions = { "ticker": "TICKER", "half-screen": "HALF_SCREEN", "full-screen": "FULL_SCREEN" };
const messageTypeOptions = { "text": "TEXT_MESSAGE", "image": "MESSAGE_IMAGE" };
const deliverMessageOptions = { "now": "NOW", "once": "SCHEDULE", "recurring": "RECURRING" };

const MessagesSettings = ({
    formData,
    setFormData,
    handleImageUpload,
    handleImageRemove,
    validated,
    titleValid,
    messageTextValid,
    messageTitle,
    dateValid,
    timeValid,
    isInitialized,
    recurringValid
}) => {
    const intl = useIntl();
    const t = (id) => intl.formatMessage({ id });
    const [minTime, setMinTime] = useState(new Date());
    
    const durationInputProps = {
        min: 1,
        max: 10,
        value: formData.duration || 1,
        label: "SELECT_DISPLAY_DURATION",
        onChange: (e) => {
            const value = Math.max(durationInputProps.min, Math.min(durationInputProps.max, e.target.value));
            setFormData({ ...formData, duration: value });
        }
    };

    const rotationInputProps = {
        min: 1,
        max: 5,
        value: formData.rotation || 1,
        label: "NUMBER_OF_ROTATION",
        onChange: (e) => {
            const value = Math.max(rotationInputProps.min, Math.min(rotationInputProps.max, e.target.value));
            setFormData({ ...formData, rotation: value });
        }
    };

    useEffect(() => {
        const now = new Date();
        if (formData.date && formData.date.toDateString() === now.toDateString() && formData.deliverMessage === 'once') {
            setMinTime(now);
        } else {
            setMinTime(new Date(0, 0, 0, 0, 0)); 
        }
    }, [formData.date, formData.deliverMessage]);

    const CustomInput = forwardRef(({ value, onClick, icon, placeholderText }, ref) => (
        <div className="position-relative">
            <input
                type="text"
                className="form-control bg-baby-blue"
                onClick={onClick}
                ref={ref}
                value={value || ''}
                readOnly
                placeholder={placeholderText}
            />
            <img src={icon} className="calendar-icon" />
        </div>
    ));

    const handleDayToggle = useCallback((index) => {
        const newRecurringDays = [...formData.recurringDays];
        newRecurringDays[index] = !newRecurringDays[index];
        setFormData({ ...formData, recurringDays: newRecurringDays });
    }, [formData, setFormData]);

    const scheduleDate = () => (
        <div className="col-6 pe-2 position-relative schedule-datetime">
            <DatePicker
                selected={formData.date}
                onChange={(date) => setFormData({ ...formData, date })}
                dateFormat="yyyy/MM/dd"
                minDate={new Date()}
                customInput={<CustomInput icon={dateicon} placeholderText={t("SELECT_DATE")} />}
                wrapperClassName={`${validated && (!dateValid ? ' is-valid' : ' is-invalid')}`}
            />
            {commonFunctions.InvalidFeedbackString(dateValid, 'SELECT_DATE')}
        </div>
    );

    const scheduleTime = () => (
        <div className="col-6 ps-2 position-relative schedule-datetime">
            <DatePicker
                selected={formData.time}
                onChange={(time) => setFormData({ ...formData, time })}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={1}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                minTime={minTime}
                maxTime={new Date().setHours(23,59,0,0)}
                customInput={<CustomInput icon={timeicon} placeholderText={t("SELECT_TIME")} />}
                wrapperClassName={`${validated && (!timeValid ? ' is-valid' : ' is-invalid')}`}
            />
            {commonFunctions.InvalidFeedbackString(timeValid, 'SELECT_TIME')}
        </div>
    );

    const renderRecurringDays = () => (
        <div className="col-6 pe-2 position-relative schedule-datetime">
            <div className="d-flex justify-content-between">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <button
                        key={day}
                        type="button"
                        className={`btn btn-sm ${formData.recurringDays[index] ? 'btn-primary selected-day' : 'btn-outline-primary unselected-day'}`}
                        onClick={() => handleDayToggle(index)}
                    >
                        {day}
                    </button>
                ))}
            </div>
            {validated && commonFunctions.InvalidFeedbackString(recurringValid, '', "d-block")}
        </div>
    );

    const handleChange = (content, delta, source, editor) => {
        if(isInitialized) {
            if (formData.messageText && formData.messageText != content) {
                document.getElementsByClassName('ql-editor')[0].innerHTML = formData.messageText;
            }
            return
        }
        const text = editor.getText();
        setFormData({ ...formData, messageText: text === '' ? '' : content });
    }

    return (
        <>
            <label className="message-title"><FormattedMessage id={messageTitle} /></label>
            <div className="col-12 col-md-5 mt-3">
                <label className="col-form-label mes-from-label-1 fw-semibold"><FormattedMessage id="SELECT_MESSAGE_STYLE" /></label>
                <RadioCheck
                    options={messageStyleOptions}
                    name="SELECT_MESSAGE_STYLE"
                    selectedOption={formData.messageStyle}
                    onChange={(e) => setFormData({ ...formData, messageStyle: e.target.value })}
                />
            </div>
            <div className="col-12 col-md-4 mt-5">
                {formData.messageStyle !== 'ticker' ? (
                    <NumberInput {...durationInputProps} />
                ) : (
                    <NumberInput {...rotationInputProps} />
                )}
            </div>
            <div className="col-12 col-md-12 mt-5">
                {formData.messageStyle === 'full-screen' && (
                    <>
                        <label className="col-form-label mes-from-label-1 fw-semibold"><FormattedMessage id="SELECT_MESSAGE_TYPE" /></label>
                        <RadioCheck
                            options={messageTypeOptions}
                            name="SELECT_MESSAGE_TYPE"
                            selectedOption={formData.messageType}
                            onChange={(e) => setFormData({ ...formData, messageType: e.target.value })}
                        />
                    </>
                )}
            </div>
            <div className="col-12 col-md-12 mt-5">
                <label className="col-form-label mes-from-label-1 fw-semibold" htmlFor="title"><FormattedMessage id="TITLE" /></label>
                <input
                    type="text"
                    className={`form-control h-54 bg-baby-blue ${validated && (!titleValid ? 'is-valid' : 'is-invalid')}`}
                    id="title"
                    name="title"
                    value={formData.title || ''}
                    placeholder={t("TYPE_YOUR_TITLE")}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
                {commonFunctions.InvalidFeedbackString(titleValid, 'TITLE')}
            </div>
            <div className="col-12 col-md-12 mt-5">
                <label className="col-form-label mes-from-label-1 fw-semibold"><FormattedMessage id="MESSAGE" /></label>
                {formData.messageType === 'text' ? (
                    <div className="mt-3">
                        <ReactQuill
                            value={formData.messageText}
                            placeholder={t("TYPE_YOUR_MESSAGE")}
                            className={`custom-quill ${formData.messageStyle === 'ticker' ? 'ticker' : ''} ${validated && (!messageTextValid ? 'is-valid' : 'is-invalid')}`}
                            onChange={handleChange}
                            modules={{
                                toolbar: [
                                    ['bold', 'italic', 'underline'],
                                ],
                            }}
                            formats={[
                                'bold', 'italic', 'underline'
                            ]}
                        />
                        {validated && messageTextValid !== 'LIMIT_LINES' && commonFunctions.InvalidFeedbackString(messageTextValid, 'MESSAGE')}
                        {messageTextValid === 'LIMIT_LINES' && (
                            <div className="invalid-feedback d-block">
                                <FormattedMessage id={messageTextValid} values={{ lines: formData.messageTextLines }} />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="mt-3 d-flex align-items-start border p-3 rounded" style={{ height: 170 }}>
                        <div className="upload-container position-relative upload-card">
                            <input
                                type="file"
                                id="fileInput"
                                className="form-control position-absolute top-0 start-0 w-100 h-100 opacity-0"
                                onChange={handleImageUpload}
                            />
                            <img src={Upload} alt="Upload Icon" className="position-absolute upload-img" />
                            <p className="position-absolute upload-p"><FormattedMessage id="JPG_FILE_2MB" /></p>
                        </div>
                        {formData.uploadedImage && (
                            <div className="uploaded-image-container ms-3 position-relative">
                                <img src={formData.uploadedImage} alt="Uploaded" style={{ width: 140, height: 140, borderRadius: '8px' }} />
                                <button
                                    type="button"
                                    className="btn-close btn-sm position-absolute ms-1 custom-close-btn"
                                    aria-label="Close"
                                    onClick={handleImageRemove}
                                ></button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="col-12 mt-5">
                <label className="col-form-label mes-from-label-1 fw-semibold"><FormattedMessage id="DELIVER_MESSAGE" /></label>
                <RadioCheck
                    options={deliverMessageOptions}
                    name="DELIVER_MESSAGE"
                    selectedOption={formData.deliverMessage}
                    onChange={(e) => setFormData({ ...formData, deliverMessage: e.target.value })}
                />
                <div id='datecontainer' className="col-12 d-flex justify-content-between mt-3">
                    {formData.deliverMessage === 'once' && scheduleDate()}
                    {formData.deliverMessage === 'recurring' && renderRecurringDays()}
                    {formData.deliverMessage !== 'now' && scheduleTime()}
                </div>
            </div>
        </>
    );
};

export default MessagesSettings;